import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { UserButton, useUser, useClerk } from "@clerk/clerk-react";
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Badge,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
  Collapse,
  InputBase,
  alpha,
  useTheme,
  useMediaQuery,
  Stack,
  Paper,
  CssBaseline,
  ThemeProvider,
  Chip,
} from "@mui/material";
import {
  DashboardOutlined as DashboardIcon,
  MovieOutlined as MovieIcon,
  TheaterComedyOutlined as TheaterIcon,
  PeopleOutlined as PeopleIcon,
  LocalActivityOutlined as BookingIcon,
  SettingsOutlined as SettingsIcon,
  MenuOpen as MenuIcon,
  MenuRounded as MenuMobileIcon,
  SearchRounded as SearchIcon,
  NotificationsNoneOutlined as BellIcon,
  TranslateOutlined as LangIcon,
  DarkModeOutlined as DarkIcon,
  LightModeOutlined as LightIcon,
  ExitToAppOutlined as LogoutIcon,
  ExpandLess,
  ExpandMore,
  Circle as DotIcon,
  GridViewOutlined as LayoutIcon,
  CalendarMonthOutlined as CalendarIcon,
  AccountCircleOutlined as UserIcon,
  ShieldOutlined as AdminIcon,
  ChevronRight,
  MoreVert as MoreIcon,
  StarBorderOutlined as StarIcon,
  KeyboardDoubleArrowRight as DoubleChevronIcon,
  KeyboardDoubleArrowLeft as DoubleChevronLeftIcon,
  Sync as SyncIcon,
  Public as IntelligenceIcon,
  CreditCardOutlined as CreditCardIcon,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import { useAdminTheme } from "./AdminThemeProvider";
import BrandLogo from "../common/BrandLogo";

const DRAWER_WIDTH = 260;
const COLLAPSED_WIDTH = 80;

const AdminLayout = ({ children }) => {
  const { mode, toggleTheme } = useAdminTheme();
  const theme = useTheme();

  const location = useLocation();
  const navigate = useNavigate();
  const { user: clerkUser } = useUser();
  const { signOut } = useClerk();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Pinned: If permanently expanded. Hover: If temporarily expanded.
  const [isPinned, setIsPinned] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openSubmenus, setOpenSubmenus] = useState({
    dashboards: true,
    management: false,
  });
  const [isScrolled, setIsScrolled] = useState(false);
  const [partnerNotifCount, setPartnerNotifCount] = useState(0);

  // Derive if sidebar is actually 'Open' (either pinned or hovered)
  const isExpanded = isPinned || hovered;

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Fetch partner notification count
  useEffect(() => {
    const fetchNotifs = async () => {
      try {
        const API =
          process.env.REACT_APP_API_URL || "http://localhost:5000/api";
        const res = await fetch(`${API}/partner/admin/notifications`);
        if (res.ok) {
          const data = await res.json();
          setPartnerNotifCount(data.pendingKYC || 0);
        }
      } catch (e) {
        /* ignore */
      }
    };
    fetchNotifs();
    const interval = setInterval(fetchNotifs, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, []);

  const togglePin = () => setIsPinned(!isPinned);
  const toggleMobileSidebar = () => setMobileOpen(!mobileOpen);

  const toggleSubmenu = (menu) => {
    if (!isExpanded) return; // Don't toggle submenus if closed (rail mode)
    setOpenSubmenus((prev) => ({ ...prev, [menu]: !prev[menu] }));
  };

  const navItems = [
    { type: "header", label: "Dashboards" },
    {
      id: "dashboards",
      label: "Dashboards",
      icon: <DashboardIcon />,
      badge: "5",
      type: "submenu",
      children: [
        { label: "CRM", path: "/admin" },
        { label: "Analytics", path: "/admin/analytics" },
        { label: "eCommerce", path: "/admin/ecommerce" },
      ],
    },
    { type: "header", label: "Apps & Pages" },
    { label: "Movie Catalog", icon: <MovieIcon />, path: "/admin/movies" },
    {
      label: "Theater Partners",
      icon: <TheaterIcon />,
      path: "/admin/theaters",
    },
    { label: "Partner KYC", icon: <AdminIcon />, path: "/admin/partners" },
    { label: "Schedule", icon: <CalendarIcon />, path: "/admin/shows" },
    { label: "Bookings", icon: <BookingIcon />, path: "/admin/bookings" },
    {
      label: "Subscriptions",
      icon: <CreditCardIcon />,
      path: "/admin/subscriptions",
    },
    { label: "Users", icon: <PeopleIcon />, path: "/admin/users" },
    {
      label: "Notifications",
      icon: <BellIcon />,
      path: "/admin/notifications",
    },
    { type: "header", label: "System Core" },
    {
      label: "Global Intelligence",
      icon: <IntelligenceIcon />,
      path: "/admin/tmdb",
    },
    { label: "System Sync", icon: <SyncIcon />, path: "/admin/data-sync" },
    { label: "Settings", icon: <SettingsIcon />, path: "/admin/settings" },
  ];

  const drawerContent = (
    <Box
      onMouseEnter={() => !isMobile && setHovered(true)}
      onMouseLeave={() => !isMobile && setHovered(false)}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        position: "relative",
        boxShadow:
          hovered && !isPinned
            ? mode === "dark"
              ? "0 4px 18px 0 rgba(0,0,0, 0.4)"
              : "0 4px 18px 0 rgba(76, 78, 100, 0.15)"
            : "none",
        transition: "all 0.2s ease",
        width: isExpanded ? DRAWER_WIDTH : COLLAPSED_WIDTH,
        borderRight: `1px solid ${theme.palette.divider}`,
      }}
    >
      {/* Brand Header */}
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "76px",
          px: isExpanded ? 3 : 0,
          transition: "all 0.2s ease",
        }}
      >
        <BrandLogo className={!isExpanded ? "collapsed-logo" : ""} />
        {isExpanded && (
          <IconButton onClick={togglePin} sx={{ ml: "auto", p: 0.5 }}>
            {isPinned ? (
              <DoubleChevronLeftIcon
                sx={{
                  fontSize: 18,
                  color: theme.palette.text.secondary,
                  opacity: 0.5,
                }}
              />
            ) : (
              <DoubleChevronIcon
                sx={{
                  fontSize: 18,
                  color: theme.palette.text.secondary,
                  opacity: 0.5,
                }}
              />
            )}
          </IconButton>
        )}
      </Box>

      {/* Menu Inner */}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          px: isExpanded ? 1.5 : 1,
          pb: 4,
          "&::-webkit-scrollbar": { width: "0px" },
        }}
      >
        <List sx={{ pt: 1 }}>
          {navItems.map((item, index) => {
            if (item.type === "header") {
              return (
                isExpanded && (
                  <Typography
                    key={index}
                    sx={{
                      color: theme.palette.text.secondary,
                      opacity: 0.4,
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      letterSpacing: "1.2px",
                      px: 2.5,
                      mb: 1.5,
                      mt: 4,
                      textTransform: "uppercase",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {item.label}
                  </Typography>
                )
              );
            }

            const isSubmenu = item.type === "submenu";
            const isActive =
              location.pathname === item.path ||
              (isSubmenu &&
                (item.children.some((c) => location.pathname === c.path) ||
                  (location.pathname === "/admin" &&
                    item.id === "dashboards")));
            const isSubmenuOpen =
              openSubmenus[item.id] || (isActive && isExpanded);

            if (isSubmenu) {
              return (
                <Box key={index} sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => toggleSubmenu(item.id)}
                    sx={{
                      mx: isExpanded ? 1 : 0.5,
                      borderRadius: "10px",
                      py: 1.2,
                      px: isExpanded ? 2 : 0,
                      justifyContent: isExpanded ? "flex-start" : "center",
                      bgcolor: isActive
                        ? alpha(theme.palette.primary.main, 0.08)
                        : "transparent",
                      color: isActive
                        ? theme.palette.primary.main
                        : theme.palette.text.secondary,
                      transition: "0.2s all ease",
                      "&:hover": {
                        bgcolor: alpha(theme.palette.text.primary, 0.05),
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        color: isActive
                          ? theme.palette.primary.main
                          : "inherit",
                        opacity: isActive ? 1 : 0.7,
                        minWidth: isExpanded ? 38 : "auto",
                        justifyContent: "center",
                      }}
                    >
                      {React.cloneElement(item.icon, { sx: { fontSize: 22 } })}
                    </ListItemIcon>
                    {isExpanded && (
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          sx: {
                            fontWeight: isActive ? 600 : 500,
                            fontSize: "14.5px",
                            color: "inherit",
                            whiteSpace: "nowrap",
                          },
                        }}
                      />
                    )}
                    {isExpanded && item.badge && (
                      <Box
                        sx={{
                          bgcolor: "#F44336",
                          color: "#fff",
                          borderRadius: "12px",
                          px: 1,
                          py: 0.1,
                          fontSize: "10px",
                          fontWeight: 800,
                          mr: 1,
                          ml: "auto",
                        }}
                      >
                        {item.badge}
                      </Box>
                    )}
                    {isExpanded &&
                      (isSubmenuOpen ? (
                        <ExpandMore sx={{ fontSize: 16, opacity: 0.5 }} />
                      ) : (
                        <ChevronRight sx={{ fontSize: 16, opacity: 0.5 }} />
                      ))}
                  </ListItemButton>
                  <Collapse
                    in={isSubmenuOpen && isExpanded}
                    timeout="auto"
                    unmountOnExit
                  >
                    <List
                      component="div"
                      disablePadding
                      sx={{ mt: 0.5, mb: 1 }}
                    >
                      {item.children.map((child, idx) => {
                        const isChildActive =
                          location.pathname === child.path ||
                          (child.path === "/admin" &&
                            (location.pathname === "/admin" ||
                              location.pathname === "/admin/"));
                        return (
                          <ListItemButton
                            key={idx}
                            onClick={() => navigate(child.path)}
                            sx={{
                              pl: 5.5,
                              py: 1,
                              borderRadius: "10px",
                              mb: 0.5,
                              mx: 1.5,
                              color: isChildActive
                                ? theme.palette.primary.main
                                : theme.palette.text.secondary,
                              bgcolor: isChildActive
                                ? alpha(theme.palette.primary.main, 0.1)
                                : "transparent",
                              "&:hover": {
                                bgcolor: alpha(
                                  theme.palette.text.primary,
                                  0.05,
                                ),
                              },
                              transition: "0.2s all",
                            }}
                          >
                            <DotIcon
                              sx={{
                                fontSize: 7,
                                mr: 2.2,
                                opacity: isChildActive ? 1 : 0.3,
                                color: isChildActive
                                  ? theme.palette.primary.main
                                  : "inherit",
                              }}
                            />
                            <ListItemText
                              primary={child.label}
                              primaryTypographyProps={{
                                sx: {
                                  fontWeight: isChildActive ? 600 : 400,
                                  fontSize: "14px",
                                  whiteSpace: "nowrap",
                                },
                              }}
                            />
                          </ListItemButton>
                        );
                      })}
                    </List>
                  </Collapse>
                </Box>
              );
            }

            return (
              <ListItem
                key={index}
                disablePadding
                sx={{ mb: 0.5, px: isExpanded ? 1 : 0.5 }}
              >
                <ListItemButton
                  onClick={() => navigate(item.path)}
                  sx={{
                    borderRadius: "10px",
                    py: 1.2,
                    px: isExpanded ? 2 : 0,
                    justifyContent: isExpanded ? "flex-start" : "center",
                    bgcolor: isActive
                      ? theme.palette.primary.main
                      : "transparent",
                    color: isActive ? "#ffffff" : theme.palette.text.secondary,
                    boxShadow: isActive
                      ? `0 6px 12px -4px ${alpha(theme.palette.primary.main, 0.5)}`
                      : "none",
                    transition: "0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      bgcolor: isActive
                        ? theme.palette.primary.main
                        : alpha(theme.palette.text.primary, 0.05),
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: "inherit",
                      opacity: isActive ? 1 : 0.7,
                      minWidth: isExpanded ? 38 : "auto",
                      justifyContent: "center",
                    }}
                  >
                    {React.cloneElement(item.icon, { sx: { fontSize: 22 } })}
                  </ListItemIcon>
                  {isExpanded && (
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        sx: {
                          fontWeight: isActive ? 600 : 500,
                          fontSize: "14.5px",
                          whiteSpace: "nowrap",
                        },
                      }}
                    />
                  )}
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        bgcolor: theme.palette.background.default,
        minHeight: "100vh",
        transition: "background-color 0.3s ease",
        maxWidth: "100vw",
        overflowX: "hidden",
      }}
    >
      {/* Sidebar Drawer */}
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? mobileOpen : true}
        onClose={toggleMobileSidebar}
        sx={{
          width: isPinned ? DRAWER_WIDTH : COLLAPSED_WIDTH,
          flexShrink: 0,
          transition: "0.2s all",
          "& .MuiDrawer-paper": {
            width: isExpanded ? DRAWER_WIDTH : COLLAPSED_WIDTH,
            transition: theme.transitions.create("width", {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            boxSizing: "border-box",
            borderRight: "none",
            boxShadow:
              isExpanded && !isPinned
                ? mode === "dark"
                  ? "0 4px 18px 0 rgba(0,0,0, 0.4)"
                  : "0 4px 18px 0 rgba(76, 78, 100, 0.15)"
                : mode === "dark"
                  ? "0 2px 10px 0 rgba(0,0,0, 0.2)"
                  : "0 2px 10px 0 rgba(76, 78, 100, 0.05)",
            overflowX: "hidden",
            bgcolor: theme.palette.background.paper,
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Global Body Framework */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          width: {
            md: `calc(100% - ${isPinned ? DRAWER_WIDTH : COLLAPSED_WIDTH}px)`,
          },
          transition: "0.2s all",
          overflowX: "hidden",
        }}
      >
        {/* Constant Position Navbar with Scroll Morph - Matches Specific Request */}
        <Box
          sx={{
            px: isScrolled ? { xs: 2.5, md: 4.5 } : 0,
            pt: 0,
            position: "sticky",
            top: 0,
            zIndex: 110,
            transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          <Paper
            elevation={0}
            sx={{
              borderBottomLeftRadius: isScrolled ? "12px" : 0,
              borderBottomRightRadius: isScrolled ? "12px" : 0,
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
              bgcolor: isScrolled
                ? alpha(theme.palette.background.paper, 0.92)
                : "transparent",
              backdropFilter: isScrolled ? "blur(12px)" : "none",
              boxShadow: isScrolled
                ? mode === "dark"
                  ? "0 4px 18px 0 rgba(0,0,0, 0.3)"
                  : "0 4px 18px 0 rgba(76, 78, 100, 0.12)"
                : "none",
              height: "64px",
              display: "flex",
              alignItems: "center",
              px: { xs: 2, md: 4 },
              transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              borderBottom: isScrolled
                ? "none"
                : `1px solid ${theme.palette.divider}`,
            }}
          >
            <IconButton
              onClick={toggleMobileSidebar}
              sx={{
                mr: 1,
                color: theme.palette.text.secondary,
                opacity: 0.68,
                display: { md: "none" },
              }}
            >
              <MenuMobileIcon sx={{ fontSize: 24 }} />
            </IconButton>

            <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
              <SearchIcon
                sx={{
                  color: theme.palette.text.secondary,
                  opacity: 0.54,
                  fontSize: 22,
                  mr: 1.5,
                }}
              />
              <InputBase
                placeholder="Search [CTRL + K]"
                sx={{
                  fontWeight: 400,
                  fontSize: "15px",
                  width: "100%",
                  color: theme.palette.text.primary,
                  opacity: 0.68,
                }}
              />
            </Box>

            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              sx={{ ml: "auto" }}
            >
              <Tooltip
                title={`Switch to ${mode === "dark" ? "Light" : "Dark"} Mode`}
              >
                <IconButton
                  onClick={toggleTheme}
                  sx={{
                    p: 1.2,
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                    color: theme.palette.primary.main,
                    "&:hover": {
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                    },
                  }}
                >
                  {mode === "dark" ? <LightIcon /> : <DarkIcon />}
                </IconButton>
              </Tooltip>

              <Tooltip title="Shortcuts">
                <IconButton
                  sx={{
                    color: theme.palette.text.secondary,
                    opacity: 0.68,
                    display: { xs: "none", sm: "inline-flex" },
                  }}
                >
                  <LayoutIcon sx={{ fontSize: 22 }} />
                </IconButton>
              </Tooltip>

              <Tooltip title="Partner Applications">
                <IconButton
                  onClick={() => navigate("/admin/partners")}
                  sx={{ color: theme.palette.text.secondary, opacity: 0.68 }}
                >
                  <Badge
                    badgeContent={partnerNotifCount}
                    color="error"
                    sx={{
                      "& .MuiBadge-badge": {
                        fontWeight: 600,
                        fontSize: "11px",
                        minWidth: "18px",
                        height: "18px",
                      },
                    }}
                  >
                    <BellIcon sx={{ fontSize: 22 }} />
                  </Badge>
                </IconButton>
              </Tooltip>

              <Divider
                orientation="vertical"
                flexItem
                sx={{ mx: 1.5, my: 1.5, borderColor: theme.palette.divider }}
              />

              {/* Secure Clerk User Controller */}
              <Box sx={{ pl: 1, display: "flex", alignItems: "center" }}>
                <Badge
                  overlap="circular"
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  variant="dot"
                  sx={{
                    "& .MuiBadge-badge": {
                      backgroundColor: "#56ca00",
                      color: "#56ca00",
                      boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
                    },
                  }}
                >
                  <UserButton
                    afterSignOutUrl="/"
                    appearance={{
                      elements: {
                        avatarBox: {
                          width: 38,
                          height: 38,
                          borderRadius: "50%",
                        },
                      },
                    }}
                  />
                </Badge>
              </Box>
            </Stack>
          </Paper>
        </Box>

        {/* Page Workspace */}
        <Box sx={{ p: { xs: 2, md: 4 }, pt: 3, flexGrow: 1 }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -15 }}
              transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
            >
              {children}
            </motion.div>
          </AnimatePresence>
        </Box>
      </Box>
    </Box>
  );
};

export default AdminLayout;
